/**
 * Handles user data storage and retrieval in browser's localStorage
 */
import { PrivilegeDto } from '@/api/ApiClient';
import { ICurrentUser } from '@/stores/authStore';
import { log } from '@/lib/utils/logger';
import { scopedLocalStorage } from '@/lib/browser/scopedStorage';

const USER_DATA_LOGICAL_KEY = 'user-data';

/**
 * Interface representing the structure of stored user data.
 * @interface
 */
interface UserData {
  /** The current user's information */
  user: ICurrentUser;
  /** List of user privileges */
  privileges: PrivilegeDto[];
  /** Expiration timestamp for the stored data */
  expiration: string;
}

/**
 * Stores user data in browser's localStorage
 */
export function storeUserData(userData: UserData) {
  if (typeof window !== 'undefined') {
    if (!userData.user.hasAnyProjectAccess || !userData.user.hasAnySampleAccess) {
      log.warn('User has no project or sample access');
    }
    try {
      const sanitizedUserData: UserData = {
        expiration: userData.expiration,
        privileges: userData.privileges,
        user: {
          id: userData.user.id,
          eppn: userData.user.eppn,
          authMethod: userData.user.authMethod,
          fullName: userData.user.fullName,
          roles: userData.user.roles.map((r) => ({ name: r.name, id: r.id })),
          selectedRole: userData.user.selectedRole,
          canSwitchRoles: userData.user.canSwitchRoles,
          usingSignInAs: userData.user.usingSignInAs,
          managedInstruments: userData.user.managedInstruments,
          techniques: userData.user.techniques,
          isAnonymous: !userData.user.eppn,
          settings: userData.user.settings,
          hasAnyProjectAccess: userData.user.hasAnyProjectAccess,
          hasAnySampleAccess: userData.user.hasAnySampleAccess,
          faculty: userData.user.faculty,
        },
      };
      scopedLocalStorage.setItem(USER_DATA_LOGICAL_KEY, JSON.stringify(sanitizedUserData));
    } catch (error) {
      log.error('[UserStorage] Error storing user data:', error);
    }
  }
}

/** True when cached user-data expiration is still in the future. */
export function isStoredSessionExpirationValid(): boolean {
  const stored = readUserdataFromStorage();
  if (!stored?.expiration) {
    return false;
  }
  return Date.now() < new Date(stored.expiration).getTime();
}

/**
 * Retrieves user data from localStorage
 */
export function readUserdataFromStorage(): UserData | undefined {
  if (typeof window !== 'undefined') {
    const data = scopedLocalStorage.getItem(USER_DATA_LOGICAL_KEY);
    if (data) {
      try {
        return JSON.parse(data) as UserData;
      } catch (error) {
        log.error('[UserStorage] Error parsing user data from storage:', error);
        return undefined;
      }
    }
    return undefined;
  }
  return undefined;
}

/** Clears MSAL browser cache keys (Entra session metadata). */
export function clearMsalCacheFromStorage() {
  if (typeof window !== 'undefined') {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('msal.')) {
        localStorage.removeItem(key);
      }
    });
  }
}

export type RemoveUserDataOptions = {
  /** When false, keep MSAL cache so SSO re-login stays frictionless (LiST-only sign-out). */
  clearMsalCache?: boolean;
};

/**
 * Removes cached LiST user data from localStorage.
 * Optionally clears MSAL cache (full SSO sign-out).
 */
export function removeUserDataFromStorage(options?: RemoveUserDataOptions) {
  if (typeof window !== 'undefined') {
    scopedLocalStorage.removeItem(USER_DATA_LOGICAL_KEY);
    if (options?.clearMsalCache !== false) {
      clearMsalCacheFromStorage();
    }
  }
}
